import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Flex,
} from "@chakra-ui/react";
import {
  FaSolarPanel,
  FaBolt,
  FaLeaf,
  FaArrowRight,
  FaCheckCircle,
  FaHandshake,
  FaBatteryFull,
  FaTrophy,
  FaHome,
  FaFacebook,
  FaInstagram,
  FaPencilAlt,
  FaCalendarAlt,
  FaHeart,
  FaGraduationCap,
  FaBriefcase,
  FaUsers,
} from "react-icons/fa";
import { keyframes } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

const chargeFill = keyframes`
  0%   { height: 0% }
  60%  { height: 100% }
  78%  { height: 100% }
  100% { height: 0% }
`;

const boltFlash = keyframes`
  0%, 52%  { opacity: 1; }
  62%       { opacity: 0.08; }
  72%       { opacity: 1; }
  82%       { opacity: 0.08; }
  92%, 100% { opacity: 1; }
`;

const panelPulse = keyframes`
  0%, 100% { opacity: 0.22; }
  50%       { opacity: 0.6; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 5px 2px 22px rgba(20,28,73,0.52), 0 0 12px rgba(198,146,20,0.2); }
  50%       { box-shadow: 5px 2px 32px rgba(20,28,73,0.7), 0 0 24px rgba(198,146,20,0.58); }
`;

const SolarCSER: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/cser"
        fallbackTitle="One Solar Australia | Corporate Social & Environmental Responsibility"
        fallbackDescription="One Solar Australia is the exclusive solar partner for yourtown Prize Homes since 2022 — powering Queensland's most celebrated prize properties with high-capacity solar and battery systems."
      />
      <NavbarWithCallToAction />

      {/* ── Floating Quote Button ── */}
      <Box
        as={RouterLink}
        to="/solar/quote"
        position="fixed"
        left="14px"
        top="50%"
        transform="translateY(-50%)"
        zIndex={100}
        w="62px"
        h="74px"
        bg={NAVY}
        borderRadius="14px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="5px"
        cursor="pointer"
        overflow="hidden"
        color="white"
        _hover={{ bg: SOLAR_GOLD, transform: "translateY(-50%) scale(1.08)" }}
        transition="background-color 0.22s, transform 0.22s"
        sx={{ animation: `${glowPulse} 2.6s ease-in-out infinite` }}
      >
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
          sx={{ animation: `${panelPulse} 2.6s ease-in-out infinite` }}
        >
          <Icon as={FaSolarPanel} boxSize={14} color={SOLAR_GOLD} />
        </Box>
        <Text fontSize="8px" fontWeight="800" letterSpacing="0.12em" textTransform="uppercase" color="white" lineHeight={1} position="relative" zIndex={2}>Get</Text>
        <Box position="relative" w="30px" h="28px" zIndex={2} flexShrink={0}>
          <svg width="30" height="28" viewBox="0 0 30 28" fill="none" style={{ position: "absolute", top: 0, left: 0, zIndex: 1 }}>
            <rect x="1" y="4" width="23" height="20" rx="3" stroke="white" strokeWidth="2.4" />
            <rect x="24.2" y="9" width="4.8" height="10" rx="2" fill="white" />
          </svg>
          <Box position="absolute" left="4px" top="7px" w="17px" h="14px" overflow="hidden" borderRadius="1.5px" zIndex={2}>
            <Box position="absolute" bottom={0} left={0} right={0} bg={SOLAR_GOLD} borderRadius="1.5px" sx={{ animation: `${chargeFill} 2.6s ease-in-out infinite` }} />
          </Box>
          <Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" zIndex={3} sx={{ animation: `${boltFlash} 2.6s ease-in-out infinite` }}>
            <Icon as={FaBolt} color="white" w="10px" h="10px" />
          </Box>
        </Box>
        <Text fontSize="8px" fontWeight="800" letterSpacing="0.12em" textTransform="uppercase" color="white" lineHeight={1} position="relative" zIndex={2}>Quote</Text>
      </Box>

      {/* ── Hero — co-brand banner image ── */}
      <Box>
        <Box
          as="img"
          src="/assets/solar-assets/yourtown-x-onesolaraustralia-1024x373.webp"
          alt="yourtown x One Solar Australia — Proudly Powering Prize Homes Since 2022"
          w="full"
          display="block"
        />
      </Box>

      {/* ── Hero text — navy block beneath banner ── */}
      <Box bg={NAVY} py={{ base: 12, md: 16 }} position="relative">
        <VStack
          position="absolute"
          left={{ base: 3, md: "18px" }}
          top="50%"
          transform="translateY(-50%)"
          spacing={4}
          zIndex={2}
        >
          <Box
            as="a"
            href="https://www.facebook.com/One-Solar-100522258324560"
            target="_blank"
            rel="noopener noreferrer"
            w={9} h={9}
            borderRadius="full"
            border="1px solid"
            borderColor="whiteAlpha.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            _hover={{ borderColor: "white", bg: "whiteAlpha.200" }}
            transition="all 0.2s"
          >
            <FaFacebook size={15} />
          </Box>
          <Box
            as="a"
            href="https://www.instagram.com/onesolar.australia.official"
            target="_blank"
            rel="noopener noreferrer"
            w={9} h={9}
            borderRadius="full"
            border="1px solid"
            borderColor="whiteAlpha.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            _hover={{ borderColor: "white", bg: "whiteAlpha.200" }}
            transition="all 0.2s"
          >
            <FaInstagram size={15} />
          </Box>
        </VStack>

        <Container maxW="container.xl">
          <VStack spacing={6} align="center" textAlign="center" color="white">
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="600" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
              Corporate Social &amp; Environmental Responsibility
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
              fontWeight="black"
              lineHeight="1.05"
              letterSpacing="tight"
              textTransform="uppercase"
            >
              Powering<br />Prize-Winning<br />Homes
            </Heading>
            <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.900" maxW="2xl" lineHeight="1.8">
              One Solar Australia has been the exclusive solar partner for yourtown Prize Homes since 2022 — engineering energy independence into Queensland's most celebrated properties.
            </Text>
            <HStack spacing={3} pt={2} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="sm"
                h="37px"
                bg="white"
                color={NAVY}
                border="1px solid"
                borderColor={SOLAR_GOLD}
                leftIcon={<Icon as={FaPencilAlt} color={SOLAR_GOLD} />}
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD, color: "white" }}
                px={5}
                transition="all 0.2s"
                textTransform="uppercase"
                letterSpacing="wider"
                fontSize="xs"
                fontWeight="700"
                sx={{ "&:hover svg": { color: "white" } }}
              >
                Free Quote
              </Button>
              <Button
                as="a"
                href="https://onegroupaustralasiacomau.pipedrive.com/scheduler/QXbMMatL/solar-assessment-booking"
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                h="37px"
                bg="transparent"
                color="white"
                border="1px solid"
                borderColor="whiteAlpha.600"
                leftIcon={<Icon as={FaCalendarAlt} color={SOLAR_GOLD} />}
                _hover={{ bg: "whiteAlpha.200", borderColor: "white" }}
                px={5}
                transition="all 0.2s"
                textTransform="uppercase"
                letterSpacing="wider"
                fontSize="xs"
                fontWeight="700"
              >
                Book Assessment
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* ── Partnership headline strip ── */}
      <Box bg="white" py={5} borderBottom="1px solid" borderColor="gray.100">
        <Container maxW="container.xl">
          <Text textAlign="center" fontSize={{ base: "sm", md: "md" }} fontWeight="600" color={NAVY}>
            Exclusive yourtown Prize Home Solar Partner — Powering Homes, Supporting Queensland's Young People
          </Text>
        </Container>
      </Box>

      {/* ── Power Your Home. EMPOWER THEIRS. ── */}
      <Box bg="gray.50" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
            <Box borderRadius="2xl" overflow="hidden" boxShadow="xl">
              <Box as="img" src="/assets/solar-assets/Mobile-5-rj01jt507gj9iaeifpw0rjbcz8c1jz4koifchwdkv2.webp" alt="yourtown and One Solar Australia partnership" w="full" h="auto" display="block" />
            </Box>
            <VStack align="flex-start" spacing={6}>
              <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
                More Than Solar
              </Text>
              <Heading size="xl" color={NAVY} lineHeight="1.15" textTransform="uppercase">
                Power Your Home.<br />
                <Box as="span" color={SOLAR_GOLD}>Empower Theirs.</Box>
              </Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="1.8">
                One Solar Australia has partnered with yourtown to supply and install solar systems on selected Prize Homes across Queensland. These homes raise vital funds for programs supporting young people and families in need — while creating life-changing opportunities for every prize winner.
              </Text>
              <Text color="gray.600" fontSize="md" lineHeight="1.8">
                Your decision to go solar does more than reduce your electricity bills. For every system installed, One Solar gives back to Queensland charities supporting vulnerable young people through housing, counselling, education, and employment pathways.
              </Text>
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="md"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<Icon as={FaArrowRight} />}
                px={7}
                transition="all 0.2s"
                fontWeight="700"
                letterSpacing="wider"
                textTransform="uppercase"
                fontSize="sm"
              >
                Get My Free Quote
              </Button>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Partnership Overview ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
              The yourtown Partnership
            </Text>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Solar That Champions Winners
            </Heading>
            <Text color="gray.600" fontSize="lg" lineHeight="1.8">
              Since 2022, One Solar Australia has served as the exclusive solar partner for selected yourtown Prize Homes. This collaboration integrates high-capacity renewable energy systems into luxury properties across South East Queensland — giving every prize winner not just a dream home, but a home that works for them from day one.
            </Text>
            <Text color="gray.600" fontSize="md" lineHeight="1.8">
              Every system we design for a yourtown property includes solar panels, battery storage, and inverters — a complete energy solution built for the long term. Our focus is simple: winners receive a home that generates its own power, cuts its own bills, and holds its value for decades.
            </Text>
            <VStack align="start" spacing={3}>
              {[
                "Comprehensive systems: panels, batteries, and inverters",
                "Properties across Sunshine Coast, Gold Coast, and hinterland",
                "Each system engineered to the property's specific size and design",
                "Maximises self-consumption and energy security for winners",
              ].map((pt) => (
                <HStack key={pt}>
                  <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} flexShrink={0} />
                  <Text fontSize="sm" color="gray.700">{pt}</Text>
                </HStack>
              ))}
            </VStack>
          </VStack>
          <Box borderRadius="2xl" overflow="hidden" h={{ base: "320px", md: "480px" }} position="relative" boxShadow="2xl">
            <Box as="img" src="/assets/solar-assets/asset%2033.jpeg" alt="One Solar Australia premium installation on prize home" w="full" h="full" objectFit="cover" />
            <Box position="absolute" bottom={0} left={0} right={0} p={5} bgGradient={`linear(to-t, ${NAVY}DD, transparent)`}>
              <Text fontSize="sm" fontWeight="700" color="white" letterSpacing="wider" textTransform="uppercase">
                Exclusive partner since 2022.
              </Text>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      {/* ── Why yourtown? ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
            The Charity Behind the Partnership
          </Text>
          <Heading size="xl" color={NAVY}>Why yourtown?</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            yourtown has supported Australian children and young people for over 60 years — running programs that create real, lasting change for those who need it most.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {[
            {
              icon: FaHome,
              title: "Safe Housing & Crisis Support",
              body: "Providing emergency shelter and stable housing for young people facing homelessness and family breakdown.",
            },
            {
              icon: FaHeart,
              title: "Mental Health & Counselling",
              body: "Delivering accessible mental health services and counselling to young people navigating crisis and uncertainty.",
            },
            {
              icon: FaGraduationCap,
              title: "Education Programs",
              body: "Connecting young Australians to pathways back into education, giving them the foundation to build their future.",
            },
            {
              icon: FaBriefcase,
              title: "Employment Pathways",
              body: "Supporting young people into long-term employment through training, mentorship, and vocational programs.",
            },
          ].map((b) => (
            <VStack
              key={b.title}
              p={7}
              bg="white"
              borderRadius="xl"
              boxShadow="md"
              spacing={4}
              align="flex-start"
              border="1px solid"
              borderColor="gray.100"
              _hover={{ borderColor: SOLAR_GOLD, boxShadow: "lg" }}
              transition="all 0.3s"
            >
              <Icon as={b.icon} boxSize={9} color={SOLAR_GOLD} />
              <Heading size="sm" color={NAVY}>{b.title}</Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="1.7">{b.body}</Text>
            </VStack>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Why This Matters Now — parallax ── */}
      <Box
        position="relative"
        color="white"
        py={20}
        backgroundImage="url('/assets/solar-assets/asset%2020.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 20, 20, 0.75)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8} textAlign="center" maxW="3xl" mx="auto">
            <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
              Impact in Action
            </Text>
            <Heading size="2xl" textShadow="0 2px 4px rgba(0,0,0,0.4)" lineHeight="1.2">
              Why This Matters Now
            </Heading>
            <Text fontSize="xl" color="rgba(255,255,255,0.9)" lineHeight="1.8" textShadow="0 1px 2px rgba(0,0,0,0.3)">
              With the cost of living rising and uncertainty impacting families across Australia, stable housing and youth support services are more critical than ever. Solar is about long-term thinking. So is supporting the next generation.
            </Text>
            <Box bg="whiteAlpha.100" borderRadius="xl" px={8} py={6} borderLeft="4px solid" borderColor={SOLAR_GOLD} textAlign="left" w="full">
              <Text fontSize="xl" fontStyle="italic" color="white" fontWeight="600" lineHeight="1.6">
                "Because real impact isn't measured in kilowatts — it's measured in lives changed."
              </Text>
            </Box>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full" pt={2}>
              {[
                "Reduce reliance on rising electricity costs",
                "Increase home value and resilience",
                "Help fund support services for vulnerable youth",
              ].map((pt) => (
                <HStack key={pt} spacing={3} bg="whiteAlpha.100" borderRadius="lg" px={5} py={4}>
                  <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} flexShrink={0} />
                  <Text fontSize="sm" color="white" fontWeight="600" textAlign="left">{pt}</Text>
                </HStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* ── Strategic Benefits for Winners ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
            Why It Matters
          </Text>
          <Heading size="xl" color={NAVY}>What Solar Delivers for Every Prize Winner</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Winning a home is one thing. Winning a home that lowers your cost of living from day one — that's what One Solar makes possible.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {[
            {
              icon: FaBolt,
              title: "Slash or Eliminate Power Bills",
              body: "Solar-powered homes dramatically reduce ongoing electricity costs. For larger prize properties with pools and air conditioning, the savings are immediate and substantial.",
            },
            {
              icon: FaBatteryFull,
              title: "Energy Security with Battery Storage",
              body: "Battery-equipped homes like Yandina keep critical appliances running through outages. Winners aren't dependent on the grid — they generate, store, and deploy their own power.",
            },
            {
              icon: FaHome,
              title: "Higher Property Value",
              body: "Solar future-proofs a home against rising energy prices. Every system we install adds measurable market value on top of the premium already built into the prize property.",
            },
            {
              icon: FaLeaf,
              title: "Environmental Leadership",
              body: "Integrating solar and battery into prize properties sends a clear message: sustainable living and luxury are not in conflict. They belong together.",
            },
          ].map((b) => (
            <VStack
              key={b.title}
              p={7}
              bg="white"
              borderRadius="xl"
              boxShadow="md"
              spacing={4}
              align="flex-start"
              border="1px solid"
              borderColor="gray.100"
              _hover={{ borderColor: SOLAR_GOLD, boxShadow: "lg" }}
              transition="all 0.3s"
            >
              <Icon as={b.icon} boxSize={9} color={SOLAR_GOLD} />
              <Heading size="sm" color={NAVY}>{b.title}</Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="1.7">{b.body}</Text>
            </VStack>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── The Smart Choice — navy band ── */}
      <Box
        position="relative"
        overflow="hidden"
        bg={NAVY}
        py={{ base: 16, md: 20 }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={14} alignItems="center">
            <VStack align="flex-start" spacing={5} color="white">
              <Text fontSize="xs" fontWeight="700" letterSpacing="widest" textTransform="uppercase" color={SOLAR_GOLD}>
                The Smart Choice — For More Than Just Energy
              </Text>
              <Heading size="xl" lineHeight="1.2">
                Business Should Be<br />About More Than<br />Transactions
              </Heading>
              <Text color="gray.300" fontSize="lg" lineHeight="1.8">
                We believe it should be about responsibility. About community. It should leave something better behind. The yourtown partnership is how we put that belief into action — engineering real energy systems into prize homes while giving back to the young Queenslanders who need it most.
              </Text>
              <Text color="gray.300" fontSize="md" lineHeight="1.8">
                Every prize home we've powered has been designed around the property, the location, and the life the winner is about to live. That's the One Solar standard — and it's the same standard we bring to every Queensland home we install.
              </Text>
              <HStack spacing={3} flexWrap="wrap">
                <Icon as={FaHandshake} color={SOLAR_GOLD} boxSize={5} />
                <Text color="gray.300" fontSize="sm">Exclusive yourtown prize home partner since 2022</Text>
              </HStack>
              <HStack spacing={3} flexWrap="wrap">
                <Icon as={FaUsers} color={SOLAR_GOLD} boxSize={5} />
                <Text color="gray.300" fontSize="sm">Every installation supports vulnerable young Queenslanders</Text>
              </HStack>
              <HStack spacing={3} flexWrap="wrap">
                <Icon as={FaTrophy} color={SOLAR_GOLD} boxSize={5} />
                <Text color="gray.300" fontSize="sm">South East Queensland, Sunshine Coast &amp; Gold Coast</Text>
              </HStack>
            </VStack>
            <VStack spacing={5}>
              <Box borderRadius="2xl" overflow="hidden" boxShadow="2xl" w="full" h={{ base: "240px", md: "320px" }}>
                <Box as="img" src="/assets/solar-assets/asset%2038.jpeg" alt="Queensland family in solar-powered home" w="full" h="full" objectFit="cover" objectPosition="center" />
              </Box>
              <Box bg="whiteAlpha.100" borderRadius="xl" px={6} py={5} borderLeft="4px solid" borderColor={SOLAR_GOLD} w="full">
                <Text color={SOLAR_GOLD} fontWeight="700" fontSize="md">Switch. Add. Upgrade. With Purpose.</Text>
                <Text color="gray.300" fontSize="sm" mt={2} lineHeight="1.7">
                  The same systems we install in yourtown prize homes are available for Queensland families. When you choose One Solar, your installation carries meaning.
                </Text>
              </Box>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box bg={SOLAR_GOLD} py={16}>
        <Container maxW="container.xl">
          <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={8}>
            <VStack align={{ base: "center", md: "start" }} spacing={3} textAlign={{ base: "center", md: "left" }}>
              <Heading size="lg" color="white">Ready to Power Your Home?</Heading>
              <Text color="whiteAlpha.900" maxW="lg" fontSize="md" lineHeight="1.7">
                Whether you're adding solar for the first time or upgrading an existing system, One Solar Australia delivers the same precision engineering we bring to every yourtown prize property.
              </Text>
            </VStack>
            <HStack spacing={4} flexShrink={0} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={NAVY}
                color="white"
                _hover={{ bg: "black" }}
                rightIcon={<FaArrowRight />}
                px={8}
                transition="all 0.2s"
              >
                Get My Free Quote
              </Button>
              <Button
                as={RouterLink}
                to="/solar"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: NAVY, borderColor: NAVY }}
                px={8}
                transition="all 0.2s"
              >
                Back to Solar
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarCSER;
